import { Utils, StringUtils, ArrayUtils, Logger, engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";

import { ImageElement, ImageElementAttributes } from "../Components/Elements/Graphics/ImageElement";
import { Tweens } from "../Components/Effects/Tweens";
import { Attributes } from "../Utils/Attributes";
import { CustomUtils } from "../Utils/CustomUtils";
import { ShapeElement } from "../Components/Elements/Graphics/ShapeElement";
import { StageController } from "../Components/StageController";

/**
 * ImageElement class is class which creates holder for image from resources. This image will be rendered to canvas.
 * Example:
 *   <ImageElement Class="ImageElement" Image="[imageId]" X="0" Y="0" />
 * @class ImageElement
 * @extends BaseElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class ImageElementExtended extends ImageElement {
    // protected running : boolean = false

    public updateByNode(node: Element): ImageElement {
        if (!node) {
            this.logError("No node specified for element update!");
            return;
        }

        if (DEBUG) {
            if (
                node.hasAttribute(ImageElementAttributes.DEBUG) &&
                node.getAttribute(ImageElementAttributes.DEBUG) === "true"
            ) {
                debugger;
            }
        }

        // if (this.running) {
        //     return this;
        // } else {
        //     this.setActive();
        // }

        if (
            !node.hasAttribute(ImageElementAttributes.TWEEN_PERSISTENT) ||
            StringUtils.parseBoolean(node.getAttribute(ImageElementAttributes.TWEEN_PERSISTENT)) != true
        ) {
            Tweens.removeTweens(this);
        }

        this._cache = Attributes.assignBooleanAttribute(node, ImageElementAttributes.CACHE, this._cache);
        this.visible = Attributes.assignBooleanAttribute(node, ImageElementAttributes.VISIBLE, this.visible);
        this.alpha = Attributes.assignFloatAttribute(node, ImageElementAttributes.ALPHA, this.alpha);
        this.blendMode = CustomUtils.getBlendMode(
            this.getBlendModeByString(
                Attributes.assignStringAttribute(node, ImageElementAttributes.BLEND_MODE, this.blendMode),
            ),
        );
        this.pivot.x = Attributes.assignIntAttribute(node, ImageElementAttributes.PIVOT_X, this.pivot.x);
        this.pivot.y = Attributes.assignIntAttribute(node, ImageElementAttributes.PIVOT_Y, this.pivot.y);

        if (node.hasAttribute(ImageElementAttributes.ROTATION)) {
            this.rotation = this.degrees2radians(parseFloat(node.getAttribute(ImageElementAttributes.ROTATION)));
        }

        if (node.hasAttribute(ImageElementAttributes.START_ROTATION)) {
            if (node.getAttribute(ImageElementAttributes.START_ROTATION) === ImageElementAttributes.START_ROTATION) {
                this.rotation = Math.floor(Math.random() * 360) / 100;
            }
        }

        if (node.hasAttribute(ImageElementAttributes.X)) {
            let values = node.getAttribute(ImageElementAttributes.X).split(";");

            if (values.length === 1) {
                this.position.x = parseFloat(values[0]);
            } else if (values.length === 2) {
                let value: number = Math.round(this.getRandom(parseFloat(values[0]), parseFloat(values[1])));
                this.position.x = value;
            }
        }
        if (node.hasAttribute(ImageElementAttributes.Y)) {
            let values = node.getAttribute(ImageElementAttributes.Y).split(";");

            if (values.length === 1) {
                this.position.y = parseFloat(values[0]);
            } else if (values.length === 2) {
                let value: number = Math.round(this.getRandom(parseFloat(values[0]), parseFloat(values[1])));
                this.position.y = value;
            }
        }
        // this.position.x = Attributes.assignIntAttribute(node, ImageElementAttributes.X, this.position.x);
        // this.position.y = Attributes.assignIntAttribute(node, ImageElementAttributes.Y, this.position.y);

        if (node.hasAttribute(ImageElementAttributes.SCALE_X)) {
            let values = node.getAttribute(ImageElementAttributes.SCALE_X).split(";");

            if (values.length === 1) {
                this.scale.x = parseFloat(values[0]);
            } else if (values.length === 2) {
                let value: number = this.getRandom(parseFloat(values[0]), parseFloat(values[1]));
                this.scale.x = value;
            }
        }

        if (node.hasAttribute(ImageElementAttributes.SCALE_Y)) {
            let values = node.getAttribute(ImageElementAttributes.SCALE_Y).split(";");

            if (values.length === 1) {
                this.scale.y = parseFloat(values[0]);
            } else if (values.length === 2) {
                let value: number = this.getRandom(parseFloat(values[0]), parseFloat(values[1]));
                this.scale.y = value;
            }
        }

        if (node.hasAttribute(ImageElementAttributes.SKEW_X)) {
            this.skew.x = this.degrees2radians(parseFloat(node.getAttribute(ImageElementAttributes.SKEW_X)));
        }

        if (node.hasAttribute(ImageElementAttributes.SKEW_Y)) {
            this.skew.y = this.degrees2radians(parseFloat(node.getAttribute(ImageElementAttributes.SKEW_Y)));
        }

        if (node.hasAttribute(ImageElementAttributes.SCALE)) {
            let values = node.getAttribute(ImageElementAttributes.SCALE).split(";");

            if (values.length === 1) {
                this.scale.x = parseFloat(values[0]);
                this.scale.y = parseFloat(values[0]);
            } else if (values.length === 2) {
                let value: number = this.getRandom(parseFloat(values[0]), parseFloat(values[1]));
                this.scale.x = value;
                this.scale.y = value;
            }
        }

        if (node.hasAttribute(ImageElementAttributes.CLEAR_MASK)) {
            if (this.maskSprite) {
                this.removeChild(this.maskSprite);
                this.maskSprite = null;
            }
            if (this.maskImage) {
                this.removeChild(this.maskImage);
                this.maskImage = null;
            }
            if (this.mask) {
                this.mask = null;
            }
        }

        if (node.hasAttribute(ImageElementAttributes.MASK)) {
            let next: Element = node.parentElement.firstElementChild;
            while (next != null) {
                if (next.nodeName === node.getAttribute(ImageElementAttributes.MASK)) {
                    let doNotHide: boolean = next.getAttribute(ImageElementAttributes.SHOW_MASK) == "true";
                    if (this.mask && this.maskImage) {
                        next.setAttribute("Visible", "true");
                        this.maskImage.updateByNode(next);
                        if (!doNotHide) {
                            next.setAttribute("Visible", "false");
                        }
                    } else {
                        switch (next.getAttribute("Class")) {
                            case "ImageElement":
                                this.maskImage = new ImageElement();
                                next.setAttribute("Visible", "true");
                                this.maskImage.create(next);
                                if (!doNotHide) {
                                    next.setAttribute("Visible", "false");
                                }
                                this.addChild(this.maskImage);
                                this.mask = this.maskImage;
                                break;
                            default:
                                let shapeElement: ShapeElement = new ShapeElement();
                                shapeElement.create(next);
                                this.mask = shapeElement;
                                break;
                        }
                    }
                }
                next = next.nextElementSibling;
            }

            if (this.mask == null) {
                if (DEBUG) {
                    this.log("ERROR no element with name: " + node.getAttribute(ImageElementAttributes.MASK));
                }
            }
        }

        if (node.hasAttribute(ImageElementAttributes.BITMAP_MASK)) {
            let image = StageController.getTextureByResourceId(node.getAttribute(ImageElementAttributes.BITMAP_MASK));

            if (image != null) {
                this.maskSprite = new PIXI.Sprite(image);
                if (node.hasAttribute(ImageElementAttributes.BITMAP_MASK_X)) {
                    this.maskSprite.position.x = parseFloat(node.getAttribute(ImageElementAttributes.BITMAP_MASK_X));
                }
                if (node.hasAttribute(ImageElementAttributes.BITMAP_MASK_Y)) {
                    this.maskSprite.position.y = parseFloat(node.getAttribute(ImageElementAttributes.BITMAP_MASK_Y));
                }
                if (!node.hasAttribute(ImageElementAttributes.BITMAP_MASK_SHOW)) {
                    this.addChild(this.maskSprite);
                    this.mask = this.maskSprite;
                }
            } else {
                if (DEBUG) {
                    this.log(
                        "ERROR" +
                            this.componentName +
                            " no image with name for mask: " +
                            node.getAttribute(ImageElementAttributes.BITMAP_MASK),
                    );
                }
            }
        }

        if (node.hasAttribute(ImageElementAttributes.TINT)) {
            let spr: PIXI.Sprite;
            if (node.getAttribute(ImageElementAttributes.TINT) === "0") {
                this.tint = null;
            } else if (node.getAttribute(ImageElementAttributes.TINT) === "dynamic") {
                this.tint = parseInt(ImageElement.DynamicTint, 16);
            } else {
                this.tint = parseInt(node.getAttribute(ImageElementAttributes.TINT), 16);
                if (this.children != null && this.children[0] != null && this.children[0] instanceof PIXI.Sprite) {
                    spr = <PIXI.Sprite>this.children[0];
                    spr.tint = this.tint;
                }
            }
        } else {
            //this.tint = 0xFFFFFF;
        }

        this.runPositionTween(node);
        this.runScaleTween(node);
        this.runRotationTween(node);
        this.runAlphaTween(node);
        this.runSkewTween(node);
        this.runTintTween(node);
        this.runTween(node);

        // NOTE(psulek) Do not join these two ifs into one, required 2 ifs for webpack optimization
        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.updateDebugInfo(this, this.getDebugInfo());
            }
        }

        return this;
    }

    public runPositionTween(node: Element): boolean {
        if (
            !node.hasAttribute(ImageElementAttributes.POSITION_TWEEN_X) &&
            !node.hasAttribute(ImageElementAttributes.POSITION_TWEEN_Y)
        ) {
            return false;
        }

        // if (this.running) {
        //     return false;
        // } else {
        //     this.setActive();
        // }

        Tweens.removeTweens(this, "position");

        let positionTweenX: Array<number>;
        let positionTweenY: Array<number>;

        let positionTweenPosition: string = Attributes.assignStringAttribute(
            node,
            ImageElementAttributes.POSITION_TWEEN_POSITION,
            null,
        );
        if (positionTweenPosition === ImageElementAttributes.POSITION_TWEEN_POSITION_VALUE.RELATIVE) {
            if (!node.hasAttribute(ImageElementAttributes.POSITION_TWEEN_X)) {
                positionTweenX = [];
                positionTweenY = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.POSITION_TWEEN_Y)).map(
                    (value) => {
                        positionTweenX.push(this.position.x);
                        return value + this.position.y;
                    },
                );
            } else if (!node.hasAttribute(ImageElementAttributes.POSITION_TWEEN_Y)) {
                positionTweenY = [];
                positionTweenX = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.POSITION_TWEEN_X)).map(
                    (value) => {
                        positionTweenY.push(this.position.y);
                        return value + this.position.x;
                    },
                );
            } else {
                positionTweenX = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.POSITION_TWEEN_X)).map(
                    (value) => value + this.position.x,
                );
                positionTweenY = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.POSITION_TWEEN_Y)).map(
                    (value) => value + this.position.y,
                );
            }
        } else {
            if (!node.hasAttribute(ImageElementAttributes.POSITION_TWEEN_X)) {
                positionTweenX = [];
                positionTweenY = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.POSITION_TWEEN_Y)).map(
                    (value) => {
                        positionTweenX.push(this.position.x);
                        return value;
                    },
                );
            } else if (!node.hasAttribute(ImageElementAttributes.POSITION_TWEEN_Y)) {
                positionTweenY = [];
                positionTweenX = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.POSITION_TWEEN_X)).map(
                    (value) => {
                        positionTweenY.push(this.position.y);
                        return value;
                    },
                );
            } else {
                positionTweenX = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.POSITION_TWEEN_X));
                positionTweenY = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.POSITION_TWEEN_Y));
            }
            if (positionTweenPosition == ImageElementAttributes.POSITION_TWEEN_POSITION_VALUE.SMOOTH) {
                if (positionTweenX.length) {
                    positionTweenX[0] = this.position.x;
                }
                if (positionTweenY.length) {
                    positionTweenY[0] = this.position.y;
                }
            }
        }

        const tweenX: Array<number> = positionTweenX || [];
        const tweenY: Array<number> = positionTweenY || [];
        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            ImageElementAttributes.POSITION_TWEEN_EASE,
            null,
        );

        let values = node.getAttribute(ImageElementAttributes.POSITION_TWEEN_DURATION).split(";");
        let tweenDuration: number = 120;

        if (values.length === 1) {
            tweenDuration = parseFloat(values[0]);
        } else if (values.length === 2) {
            let value: number = Math.round(this.getRandom(parseFloat(values[0]), parseFloat(values[1])));
            tweenDuration = value;
        } else if (values.length === 3) {
            let value: number = Math.round(
                parseFloat(values[2]) * this.getRandom(parseFloat(values[0]), parseFloat(values[1])),
            );
            tweenDuration = value;
        }

        let tweenDelay: number = 0;
        if (node.hasAttribute(ImageElementAttributes.POSITION_TWEEN_DELAY)) {
            let tweenDelayArray: Array<number> = node
                .getAttribute(ImageElementAttributes.POSITION_TWEEN_DELAY)
                .split(";")
                .map((value) => parseInt(value));
            tweenDelay =
                tweenDelayArray.length == 2
                    ? Math.round(tweenDelayArray[0] + Math.random() * (tweenDelayArray[1] - tweenDelayArray[0]))
                    : tweenDelayArray[0];
        }
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            ImageElementAttributes.POSITION_TWEEN_LOOP,
            null,
        );
        const keyTimes: Array<number> = node.hasAttribute(ImageElementAttributes.POSITION_TWEEN_KEY_TIMES)
            ? ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.POSITION_TWEEN_KEY_TIMES))
            : null;

        let bezierType: string;
        let bezierCurviness: number;
        let bezierPoints: Object;
        let bezierObject: Object;
        let bezierAutoRotate: boolean;

        if (node.hasAttribute(ImageElementAttributes.POSITION_TWEEN_BEZIER_POINTS)) {
            bezierType = Attributes.assignStringAttribute(
                node,
                ImageElementAttributes.POSITION_TWEEN_BEZIER_TYPE,
                "thru",
            );
            bezierCurviness = Attributes.assignIntAttribute(
                node,
                ImageElementAttributes.POSITION_TWEEN_BEZIER_CURVINESS,
                1.0,
            );
            bezierPoints = this.formatPoints(node.getAttribute(ImageElementAttributes.POSITION_TWEEN_BEZIER_POINTS));
            bezierAutoRotate = Attributes.assignBooleanAttribute(
                node,
                ImageElementAttributes.POSITION_TWEEN_BEZIER_AUTOROTATE,
                false,
            );

            if (bezierType && bezierCurviness && bezierPoints) {
                bezierObject = {
                    type: bezierType,
                    curviness: bezierCurviness,
                    values: bezierPoints,
                    autoRotate: bezierAutoRotate ? ["x", "y", "rotation", Math.PI / 2, true] : false,
                };
            }
        }

        Tweens.positionTween(this, {
            bezier: bezierObject,
            tweenX,
            tweenY,
            tweenEase,
            tweenDuration,
            tweenDelay,
            tweenLoop,
            keyTimes,
        });

        // Tweens.positionTween(this, {bezier: bezierObject, tweenX, tweenY, tweenEase, tweenDuration, tweenDelay, tweenLoop, keyTimes,
        //     callbackType: 'onComplete',
        //     callbackFunction: this.setInactive,
        //     callbackScope: this
        // });
    }

    public getRandom(min: number, max: number): number {
        return Math.random() * (max - min) + min;
    }

    // protected setInactive() {
    //     this.running = false;
    // }
    //
    // protected setActive() {
    //     this.running = true;
    // }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/ImageElementExtended.ts
 **/